import { useState, useEffect } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../components/Generic/Toast/NotificationToast";
import { useAppState } from "../context/StateContext";
import { Error } from "../models/error";
import { LiftvanConfig } from "../models/liftvan.config";
import { Filters, Searches, Sort, Sorts } from "../models/shipeezi.table";
import { StorageBox, StorageBoxDto } from "../models/storage.box";
import LiftvanConfigService from "../services/liftvan.config.service";
import StorageBoxService from "../services/storage.box.service";
import { checkError } from "../util/checkError";
import getRelatedFilters from "../util/getRelatedFilters";

export const useStorageBoxHook = () => {
  const { currentUser } = useAppState();

  const [storageBoxesData, setStorageBoxesData] = useState<StorageBox[]>([]);

  const [filters, setFilters] = useState<Filters>({
    "storageBox.isActive": ["1"],
    "branches.id": currentUser?.branches?.map((branch) => String(branch.id)) || [],
  });
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Sorts>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(15);
  const [total, setTotal] = useState<number>(0);
  const [hasError, setHasError] = useState<boolean>();
  const [hasErrorUpload, setHasErrorUpload] = useState<boolean>();

  const { t: translate } = useTranslation();

  enum StorageBoxProperties {
    STORAGE_STATUS = "storageStatus",
    STORAGE_BOX_TYPE = "storageBoxType",
    WAREHOUSES = "warehouses",
  }

  const handleSort = (sort: Sort[]) => {
    const sorting: Sorts = [];

    sort.forEach((sort) => {
      switch (sort.columnName) {
        case StorageBoxProperties.STORAGE_STATUS:
          sorting.push([StorageBoxProperties.STORAGE_STATUS, "name", sort.direction]);
          break;
        case StorageBoxProperties.STORAGE_BOX_TYPE:
          sorting.push([StorageBoxProperties.STORAGE_BOX_TYPE, "name", sort.direction]);
          break;
        case StorageBoxProperties.WAREHOUSES:
          sorting.push([StorageBoxProperties.WAREHOUSES, "name", sort.direction]);
          break;
        default:
          sorting.push([sort.columnName, sort.direction]);
      }
    });

    return sorting;
  };

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      setHasError(undefined);
    }
  }, [hasError]);

  useEffect(() => {
    if (hasErrorUpload !== undefined && !hasErrorUpload) {
      setHasErrorUpload(undefined);
    }
  }, [hasErrorUpload]);

  const getStorageBoxesList = () =>
    useQuery(
      ["storage.box.list", page, pageSize, searches, filters, sorting],
      () =>
        StorageBoxService.getAll({
          page,
          pageSize,
          companyId: currentUser?.companyId,
          isFleet: 0,
          singleUse: 0,
          sortBy:
            sorting && sorting?.length > 0
              ? JSON.stringify([...sorting, ["updatedAt", "desc"]])
              : JSON.stringify([["updatedAt", "desc"]]),
          include: JSON.stringify([
            {
              association: "storageStatus",
              where: {
                ...getRelatedFilters(filters, "storageStatus"),
              },
            },
            {
              association: "storageBoxType",
              where: {
                ...getRelatedFilters(filters, "storageBoxType"),
              },
            },
            {
              association: "branches",
              where: {
                ...getRelatedFilters(filters, "branches"),
              },
              required: false,
            },
            {
              association: "warehouse",
            },
            {
              association: "warehouses",
              required: true,
              where: {
                ...getRelatedFilters(filters, "warehouses"),
              },
            },
          ]),
          searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
          ...getRelatedFilters(filters, "storageBox"),
        }),
      {
        enabled: !!currentUser?.companyId,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          if (response && response.data) setStorageBoxesData(response.data);
        },
      }
    );

  const getStorageBoxesCount = () =>
    useQuery(
      ["storage.box.count", filters, searches],
      () =>
        StorageBoxService.count({
          companyId: currentUser?.companyId,
          isFleet: 0,
          singleUse: 0,
          include: JSON.stringify([
            {
              association: "storageStatus",
              where: {
                ...getRelatedFilters(filters, "storageStatus"),
              },
            },
            {
              association: "storageBoxType",
              where: {
                ...getRelatedFilters(filters, "storageBoxType"),
              },
            },
            {
              association: "warehouses",
              where: {
                ...getRelatedFilters(filters, "warehouses"),
              },
            },
            {
              association: "branches",
              orWhere: true,
              where: [
                {
                  ...getRelatedFilters(filters, "branches"),
                },
                {
                  id: null
                }
              ],
              required: false,
            },
          ]),
          distinctCount: true,
          searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
          ...getRelatedFilters(filters, "storageBox"),
        }),
      {
        enabled: !!currentUser?.companyId,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          setTotal(response?.data || 0);
        },
      }
    );

  const { refetch: refetchStorageBoxes } = getStorageBoxesList();
  const { refetch: refetchStorageBoxesCount } = getStorageBoxesCount();

  const handleSearch = (values: Searches) => {
    setSorting(undefined);
    setSearches(values);
    setPage(0);
  };

  const handleFilter = (values: Filters) => {
    setFilters(values);
    setPage(0);
  };

  const handleSorting = (sorts: Sort[]) => {
    setSorting(handleSort(sorts));
  };

  const handleCurrentPage = (page: number) => setPage(page - 1);

  const handleGetDefaultFilters = () => Object.values(filters)?.[0];
  const handleGetDefaultBranchFilters = () => Object.values(filters)?.[1];

  const useCreateStorageBox = (
    callback?: (resopnse: StorageBox, variables?: StorageBoxDto) => void
  ) => {
    return useMutation(
      ["storage.box.create"],
      (storageBox: StorageBoxDto) => StorageBoxService.create(storageBox),
      {
        onSuccess: (response, variables) => {
          NotificationToast.success(translate("operationPerformedSuccessfully"));
          setHasError(false);
          // refetchStorageBoxes();
          // refetchStorageBoxesCount();
          if (callback) {
            callback(response.data, variables);
          }
        },
        onError: (error: AxiosError<Error>) => {
          checkError(error, translate("anErrorOccurredTryAgain"));
          setHasError(true);
        },
      }
    );
  };

  const useCreateLiftvanConfig = (callback?: (response?: any) => void) => {
    return useMutation(
      ["liftvan.config.create"],
      (liftvanConfig: LiftvanConfig) => LiftvanConfigService.create(liftvanConfig),
      {
        onSuccess: () => {
          NotificationToast.success(translate("operationPerformedSuccessfully"));
          setHasError(false);
          refetchStorageBoxes();
          refetchStorageBoxesCount();
          if (callback) callback();
        },
        onError: (error: AxiosError<Error>) => {
          checkError(error, translate("anErrorOccurredTryAgain"));
          setHasError(true);
        },
      }
    );
  };

  const useUpdateLiftvanConfig = (callback?: (variables: LiftvanConfig) => void) => {
    return useMutation(
      ["liftvan.config.update"],
      (liftvanConfig: LiftvanConfig) => LiftvanConfigService.update(liftvanConfig),
      {
        onSuccess: (_, variables) => {
          NotificationToast.success(translate("operationPerformedSuccessfully"));
          setHasError(false);
          refetchStorageBoxes();
          refetchStorageBoxesCount();
          if (callback) callback(variables);
        },
        onError: (error: AxiosError<Error>) => {
          checkError(error, translate("anErrorOccurredTryAgain"));
          setHasError(true);
        },
      }
    );
  };

  const useCreateStorageBoxesList = () => {
    return useMutation(
      ["storage.box.create.list"],
      ({ storageBoxes }: { storageBoxes: StorageBoxDto[] }) =>
        StorageBoxService.createList(storageBoxes),
      {
        onError: (error: AxiosError<Error>) => {
          checkError(error, translate("anErrorOccurredTryAgain"));
          setHasErrorUpload(true);
        },
        onSuccess: (response) => {
          if (response.data && response)
            NotificationToast.success(translate("yourStorageBoxesAreBeingUploaded"));
          setHasErrorUpload(false);
          refetchStorageBoxes();
          refetchStorageBoxesCount();
        },
      }
    );
  };

  const useUpdateStorageBox = () => {
    return useMutation(
      ["storage.box.update"],
      ({ id, storageBox }: { id: number; storageBox: StorageBoxDto }) =>
        StorageBoxService.update(id, storageBox),
      {
        onSuccess: () => {
          NotificationToast.success(translate("operationPerformedSuccessfully"), {
            id: "request.notification.success.storage.box",
          });
          setHasError(false);
          refetchStorageBoxes();
          refetchStorageBoxesCount();
        },
        onError: (error: AxiosError<Error>) => {
          checkError(error, translate("anErrorOccurredTryAgain"));
          setHasError(true);
        },
      }
    );
  };

  return {
    page,
    total,
    pageSize,
    hasError,
    setPageSize,
    handleSearch,
    handleFilter,
    handleSorting,
    hasErrorUpload,
    storageBoxesData,
    handleCurrentPage,
    handleGetDefaultFilters,
    handleGetDefaultBranchFilters,

    getStorageBoxesList,
    useUpdateStorageBox,
    useCreateStorageBox,
    useCreateLiftvanConfig,
    useUpdateLiftvanConfig,
    getStorageBoxesCount,
    useCreateStorageBoxesList,
  };
};
